import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { BarChart3 } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

interface BenchmarkComparisonProps {
  portfolioReturn: number;
}

const BENCHMARKS = [
  { symbol: 'SPY', label: 'S&P 500' },
  { symbol: 'NIFTY50', label: 'Nifty 50' },
  { symbol: 'BTC', label: 'Bitcoin' },
];

export const BenchmarkComparison = ({ portfolioReturn }: BenchmarkComparisonProps) => {
  const { data: benchmarkPrices } = useQuery({
    queryKey: ['benchmark-prices'],
    queryFn: async () => {
      const { data } = await supabase
        .from('market_prices')
        .select('symbol, change_percent')
        .in('symbol', BENCHMARKS.map(b => b.symbol));
      return data || [];
    },
    staleTime: 300_000,
  });

  const items = BENCHMARKS.map(b => {
    const mp = benchmarkPrices?.find(p => p.symbol === b.symbol);
    return { ...b, returnPct: mp?.change_percent ?? null };
  });

  const maxAbs = Math.max(
    Math.abs(portfolioReturn),
    ...items.map(i => Math.abs(i.returnPct ?? 0)),
    1
  );

  const Bar = ({ label, value, highlight }: { label: string; value: number | null; highlight?: boolean }) => {
    if (value === null) return (
      <div className="flex items-center justify-between py-1.5">
        <span className="text-xs text-muted-foreground">{label}</span>
        <span className="text-[10px] text-muted-foreground">N/A</span>
      </div>
    );
    const width = Math.max(4, (Math.abs(value) / maxAbs) * 100);
    const positive = value >= 0;
    const color = positive ? 'bg-[hsl(var(--accent-green))]' : 'bg-[hsl(var(--accent-red))]';
    const textColor = positive ? 'text-[hsl(var(--accent-green))]' : 'text-[hsl(var(--accent-red))]';
    const outperforms = highlight && portfolioReturn > value;

    return (
      <div className="flex items-center gap-3 py-1.5">
        <span className={`text-xs w-20 truncate ${highlight ? 'font-semibold text-foreground' : 'text-muted-foreground'}`}>{label}</span>
        <div className="flex-1 h-4 bg-muted/30 rounded-full overflow-hidden relative">
          <div className={`h-full rounded-full ${color} transition-all`} style={{ width: `${width}%` }} />
        </div>
        <span className={`text-xs font-mono tabular-nums w-16 text-right ${textColor}`}>
          {value >= 0 ? '+' : ''}{value.toFixed(1)}%
        </span>
        {outperforms !== undefined && !highlight && (
          <span className={`text-[9px] ${portfolioReturn > value ? 'text-[hsl(var(--accent-green))]' : 'text-[hsl(var(--accent-red))]'}`}>
            {portfolioReturn > value ? '▲' : '▼'}
          </span>
        )}
      </div>
    );
  };

  return (
    <Card className="border-border/30">
      <CardHeader className="pb-2 px-4 pt-4">
        <CardTitle className="text-sm font-heading flex items-center gap-1.5">
          <BarChart3 className="w-4 h-4 text-primary" /> vs Benchmarks
        </CardTitle>
      </CardHeader>
      <CardContent className="px-4 pb-4">
        <Bar label="Your Portfolio" value={portfolioReturn} highlight />
        {items.map(i => (
          <Bar key={i.symbol} label={i.label} value={i.returnPct} />
        ))}
      </CardContent>
    </Card>
  );
};
